import React from 'react'
import { PAYMENT_TYPE, PAYMENT_TYPE_CODE } from '../constants/global'
import {
  PARAM_APIKEY,
  PARAM_HIDE_PAYMENT_METHOD,
  PARAM_IS_EMBEDDED_VIEW,
  PARAM_PAYMENT_METHOD,
  PARAM_SHOW_PHONE_INPUT,
  PARAM_USERNAME_PREFIX,
  PARAM_BACK_TO_HOME_MINI_APP_URL
} from '../constants/params'
import { SessionStorageManager } from '../helper/localStorage'
import { getUrlParamValue, smartParseParam } from '../helper/params'

export const AppParamsContext = React.createContext(null)

const toBoolean = (value) => value === true || value === 1 || value === '1' || value === 'true'

const normalizeToArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean)
  if (value === undefined || value === null || value === '') return []
  return [value]
}

const getFirstUrlParamValue = (keys = []) => {
  for (const key of normalizeToArray(keys)) {
    const value = getUrlParamValue(key)
    if (value !== null && value !== undefined) {
      return value
    }
  }

  return undefined
}

const getFirstSessionValue = (keys = []) => {
  for (const key of normalizeToArray(keys)) {
    const value = SessionStorageManager.getItem(key)
    if (value !== null && value !== undefined && value !== '') {
      return value
    }
  }

  return undefined
}

const persistSessionValue = (keys = [], value) => {
  normalizeToArray(keys).forEach((key) => {
    SessionStorageManager.setItem(key, value)
  })
}

const resolveParamsMiniApp = (config) => {
  const paramKeys = config.paramKeys || config.paramKey
  const storageKeys = config.storageKeys || config.storageKey || paramKeys
  const param = getFirstUrlParamValue(paramKeys)

  if (param !== null && param !== undefined) {
    persistSessionValue(storageKeys, param)
  }

  const raw =
    param ??
    getFirstSessionValue(storageKeys) ??
    (typeof config.getEnvValue === 'function' ? config.getEnvValue() : undefined) ??
    smartParseParam(process.env?.[config.envKey]) ??
    config.defaultValue

  return config.parser ? config.parser(raw) : raw
}

const PARAMS_MINIAPP_SCHEMA = {
  apikey: {
    paramKeys: [PARAM_APIKEY],
    storageKeys: [PARAM_APIKEY],
    getEnvValue: () => smartParseParam(process.env.REACT_APP_APIKEY),
    defaultValue: undefined,
    parser: (value) => value
  },
  isEmbeddedView: {
    paramKey: PARAM_IS_EMBEDDED_VIEW,
    storageKey: PARAM_IS_EMBEDDED_VIEW,
    envKey: 'REACT_APP_PARAM_IS_EMBEDDED_VIEW',
    defaultValue: false,
    parser: toBoolean
  },
  showPhoneInput: {
    paramKey: PARAM_SHOW_PHONE_INPUT,
    storageKey: PARAM_SHOW_PHONE_INPUT,
    envKey: 'REACT_APP_SHOW_PHONE_INPUT',
    defaultValue: true,
    parser: toBoolean
  },
  paymentMethodType: {
    paramKey: PARAM_PAYMENT_METHOD,
    storageKey: PARAM_PAYMENT_METHOD,
    envKey: 'REACT_APP_PAYMENT_METHOD',
    defaultValue: undefined,
    parser: (value) => {
      const paymentMethod = String(value || '').toLowerCase()
      const paymentMethods = Object.values(PAYMENT_TYPE_CODE).map((item) => item.toLowerCase())

      if (paymentMethods.includes(paymentMethod)) {
        const key = Object.keys(PAYMENT_TYPE_CODE).find((item) => PAYMENT_TYPE_CODE[item].toLowerCase() === paymentMethod)
        return PAYMENT_TYPE[key]
      }

      return undefined
    }
  },
  hidePaymentMethod: {
    paramKey: PARAM_HIDE_PAYMENT_METHOD,
    storageKey: PARAM_HIDE_PAYMENT_METHOD,
    envKey: 'REACT_APP_HIDE_PAYMENT_METHOD',
    defaultValue: false,
    parser: toBoolean
  },
  usernamePrefix: {
    paramKey: PARAM_USERNAME_PREFIX,
    storageKey: PARAM_USERNAME_PREFIX,
    envKey: 'REACT_APP_USERNAME_PREFIX',
    defaultValue: false,
    parser: (value) => value
  },
  backToHomeMiniAppUrlParam: {
    paramKeys: [PARAM_BACK_TO_HOME_MINI_APP_URL],
    storageKeys: [PARAM_BACK_TO_HOME_MINI_APP_URL],
    envKey: '',
    defaultValue: '',
    parser: (v) => v
  }
}

export const AppParamsContextProvider = (props) => {
  const paramsMiniApp = React.useMemo(() => {
    const params = {}

    Object.keys(PARAMS_MINIAPP_SCHEMA).forEach((key) => {
      params[key] = resolveParamsMiniApp(PARAMS_MINIAPP_SCHEMA[key])
    })

    return params
  }, [])

  return React.createElement(AppParamsContext.Provider, { value: paramsMiniApp }, props.children)
}

export const useAppParamsContext = () => {
  const context = React.useContext(AppParamsContext)
  if (!context) {
    throw new Error('useAppParamsContext must be used within AppParamsContextProvider')
  }
  return context
}
